import { Row } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { z } from "zod";

import { loadLocale } from "@smart/itops-locale-dom";
import { TimePeriod } from "@smart/itops-sb-design-system-dom";
import { Staff, useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { extractId } from "@smart/itops-types-basic";

import { ExtendedGqlSubmission, GqlSubmissionSummary } from "../../types";
import { isWithinPeriod } from "../utils/time-filters";

export const sortSchema = z.array(
  z.object({ id: z.string(), desc: z.boolean() }),
);

export const typeOfMatterFilterOptions = [
  { value: "both", label: "Both" },
  { value: "lead", label: "Lead" },
  { value: "matter", label: "Matter" },
];

export const typeOfMatterFilterAllOption = "both";

export const getOnRowClick =
  (
    navigate:
      | NonNullable<ReturnType<typeof useSmokeballApp>["host"]>["navigate"]
      | undefined,
  ) =>
  (row: GqlSubmissionSummary) => {
    if (row.uri && row.matter?.id) {
      navigate?.({
        target: "matter-intake-tab",
        matterId: row.matter.id,
        submissionId: extractId(row.uri),
      });
    }
  };

export const NO_VALUE = "-";

export const getFormMatterTypeName = (row: GqlSubmissionSummary) => {
  const matterTypeId = row.matter?.matterTypeId;
  if (!matterTypeId) return undefined;

  const matterType = row.form.values.matterTypes.find(
    (mt) => mt.id === matterTypeId,
  );

  return matterType?.name;
};

export const getStaffName = ({
  staff,
  staffId,
}: {
  staff: Staff[] | undefined;
  staffId: string | null | undefined;
}) => {
  if (!staffId) return NO_VALUE;

  const staffMember = staff?.find((s) => s.id === staffId);
  if (!staffMember) return NO_VALUE;

  return [staffMember.firstName, staffMember.lastName]
    .filter(Boolean)
    .join(" ");
};

export const filterBySelection = (
  row: Row<GqlSubmissionSummary>,
  columnId: string,
  filterValue: any,
) => {
  const value = row.getValue(columnId);

  return (
    !Array.isArray(filterValue) ||
    filterValue.length === 0 ||
    (!value && filterValue.includes("")) ||
    filterValue.includes(value)
  );
};

export const filterByDatePeriod = (
  row: Row<GqlSubmissionSummary>,
  columnId: string,
  filterValue: { period: TimePeriod; range?: { from?: Date; to?: Date } },
) => {
  if (filterValue.period === "allTime") return true;
  const value = row.getValue<string | null | undefined>(columnId);
  if (!value) return false;
  return isWithinPeriod(
    filterValue.period,
    parseISO(value),
    loadLocale().country,
    filterValue.range,
  );
};

export const filterByTypeOfMatter = (
  row: Row<ExtendedGqlSubmission>,
  columnId: string,
  filterValue: any,
) =>
  !filterValue ||
  filterValue === typeOfMatterFilterAllOption ||
  row.getValue(columnId) === filterValue;

export const sortByStaffName = ({
  staff,
  staffAId,
  staffBId,
}: {
  staff: Staff[] | undefined;
  staffAId: string | null | undefined;
  staffBId: string | null | undefined;
}) => {
  const lowerCaseStaffNameA = getStaffName({
    staff,
    staffId: staffAId,
  }).toLocaleLowerCase();
  const lowerCaseStaffNameB = getStaffName({
    staff,
    staffId: staffBId,
  }).toLocaleLowerCase();

  if (lowerCaseStaffNameA === lowerCaseStaffNameB) return 0;
  return lowerCaseStaffNameA > lowerCaseStaffNameB ? 1 : -1;
};

export const extendSubmissions = (
  submissions: GqlSubmissionSummary[],
  supplimentalMatterTypes: { id: string; name: string }[],
): ExtendedGqlSubmission[] =>
  submissions.map((submission) => {
    const derivedSubmissionStatus =
      submission.values.status === "draft" &&
      submission.values.communicationMethod === "internalUse"
        ? "prefilled"
        : submission.values.status;
    const getTypeOfMatter = () => {
      if (!submission.matter) return undefined;
      return submission.matter.isLead ? "lead" : "matter";
    };

    const matterTypeName =
      getFormMatterTypeName(submission) ||
      supplimentalMatterTypes?.find(
        (mt) => mt.id === submission.matter?.matterTypeId,
      )?.name ||
      NO_VALUE;

    return {
      ...submission,
      derivedSubmissionStatus,
      typeOfMatter: getTypeOfMatter(),
      matterTypeName,
    };
  });
